import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../utils/api';

/**
 * SPV Series Panel — per-series "last known" invoice numbers from ANAF SPV.
 *
 * SPV is the numbering authority (see docs/features/SPV_ANCHORED_NUMBERING_PLAN.md).
 * This panel shows the cached last-known state, lets the accountant sync now,
 * and (only for companies that never issued an invoice) set a manual seed.
 */
function SpvSeriesPanel({ clientId }) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [liveCheck, setLiveCheck] = useState({ loading: false, invoices: null, checkedAt: null, error: null });
  const [seedPrefix, setSeedPrefix] = useState('');
  const [seedStart, setSeedStart] = useState('');
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/clients/${clientId}/spv-series`);
      if (!res.ok) throw new Error('Eroare la încărcarea seriilor SPV');
      const data = await res.json();
      setState({ status: 'success', data, error: null });
    } catch (e) {
      setState({ status: 'error', data: null, error: e.message });
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const handleLastInvoices = async () => {
    setLiveCheck({ loading: true, invoices: null, checkedAt: null, error: null });
    try {
      const res = await apiFetch(`/api/clients/${clientId}/spv-series/last-invoices`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) {
        setLiveCheck({ loading: false, invoices: data?.invoices || null, checkedAt: data?.checked_at,
                       error: data?.sync_error || 'ANAF indisponibil — se afișează ultima stare cunoscută.' });
      } else {
        setLiveCheck({ loading: false, invoices: data.invoices, checkedAt: data.checked_at, error: null });
      }
      await load(); // series table may have advanced too
    } catch (e) {
      setLiveCheck({ loading: false, invoices: null, checkedAt: null, error: e.message });
    }
  };

  const handleSeed = async () => {
    setMessage(null);
    try {
      const res = await apiFetch(`/api/clients/${clientId}/spv-series/seed`, {
        method: 'POST',
        body: JSON.stringify({ prefix: seedPrefix, start: seedStart }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.msg || 'Eroare la setarea seriei');
      setMessage({ type: 'success', text: `Serie setată: ${seedPrefix || '(fără prefix)'} începând de la ${seedStart}.` });
      setState({ status: 'success', data: { ...data, enabled: state.data?.enabled }, error: null });
      setSeedPrefix('');
      setSeedStart('');
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  if (state.status === 'loading') return <div className="section-card"><div className="section-content">Se încarcă seriile SPV…</div></div>;
  if (state.status === 'error') return <div className="section-card"><div className="section-content" style={{ color: '#ff6b6b' }}>{state.error}</div></div>;

  const { series = {}, last_sync_at: lastSyncAt, manual_seed: manualSeed, enabled } = state.data || {};
  const seriesEntries = Object.entries(series);

  return (
    <div className="section-card">
      <div className="section-header">
        <h2 className="section-title">Serii de facturare (SPV)</h2>
        <p className="section-subtitle">
          Ultimele numere cunoscute din SPV — SPV este autoritatea de numerotare.
          {lastSyncAt
            ? ` Ultima sincronizare: ${new Date(lastSyncAt).toLocaleString('ro-RO')}.`
            : ' Nu a fost încă sincronizat.'}
        </p>
        <div>
          <button className="review-btn" onClick={handleLastInvoices} disabled={liveCheck.loading}>
            {liveCheck.loading ? 'Se interoghează ANAF…' : 'Verifică ANAF acum — ultimele facturi și serii'}
          </button>
        </div>
      </div>
      <div className="section-content">
        {!enabled && (
          <div style={{ opacity: 0.7, marginBottom: '1rem' }}>
            Notă: numerotarea ancorată în SPV este momentan dezactivată (SPV_NUMBERING_ENABLED=false).
            Sincronizarea construiește cache-ul, dar alocarea și verificarea la trimitere nu sunt active.
          </div>
        )}

        {message && (
          <div className={message.type === 'error' ? 'error-message' : 'success-message'} style={{ marginBottom: '1rem' }}>
            {message.text}
          </div>
        )}

        {liveCheck.loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', margin: '1rem 0' }}>
            <div className="spv-live-spinner" aria-label="Se încarcă" />
            <span style={{ opacity: 0.8 }}>Se interoghează ANAF în timp real…</span>
          </div>
        )}
        {!liveCheck.loading && (liveCheck.invoices || liveCheck.error) && (
          <div style={{ margin: '1rem 0' }}>
            <h3 style={{ fontSize: '1rem', marginBottom: '0.4rem' }}>
              Ultimele facturi în ANAF
              {liveCheck.checkedAt && (
                <span style={{ fontWeight: 400, opacity: 0.7 }}>
                  {' '}— verificat la {new Date(liveCheck.checkedAt).toLocaleString('ro-RO')}
                </span>
              )}
            </h3>
            {liveCheck.error && (
              <div className="error-message" style={{ marginBottom: '0.6rem' }}>{liveCheck.error}</div>
            )}
            {liveCheck.invoices && liveCheck.invoices.length > 0 ? (
              <div className="lines-table-container">
                <table className="lines-table">
                  <thead className="lines-table-head">
                    <tr><th>Număr</th><th>Data</th><th>Beneficiar</th><th>Total</th></tr>
                  </thead>
                  <tbody className="lines-table-body">
                    {liveCheck.invoices.map((inv, i) => (
                      <tr key={i}>
                        <td style={{ fontFamily: 'monospace' }}>{inv.numar}</td>
                        <td>{inv.data || '—'}</td>
                        <td>{inv.beneficiar || '—'}</td>
                        <td>{inv.total != null ? Number(inv.total).toLocaleString('ro-RO') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (!liveCheck.error && <div style={{ opacity: 0.8 }}>Nicio factură găsită în SPV (orizont 60 de zile).</div>)}
          </div>
        )}

        {seriesEntries.length > 0 ? (
          <div className="lines-table-container">
            <table className="lines-table">
              <thead className="lines-table-head">
                <tr><th>Serie</th><th>Ultimul număr cunoscut</th><th>Văzut în SPV la</th></tr>
              </thead>
              <tbody className="lines-table-body">
                {seriesEntries.map(([prefix, info]) => (
                  <tr key={prefix || '(fara prefix)'}>
                    <td>{prefix || <em>(fără prefix)</em>}</td>
                    <td style={{ fontFamily: 'monospace' }}>{info.last_number}</td>
                    <td>{info.last_seen_at ? new Date(info.last_seen_at).toLocaleString('ro-RO') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ opacity: 0.8 }}>
            <p>Nicio serie cunoscută încă. Apăsați „Verifică ANAF acum" pentru a citi istoricul din SPV.</p>
            <p style={{ marginTop: '0.75rem' }}>
              <strong>Doar dacă firma nu a emis niciodată facturi</strong> (SPV nu are nimic de oferit),
              setați seria manual:
            </p>
            <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.5rem', alignItems: 'center' }}>
              <input
                placeholder="Prefix serie (ex: ABC)"
                value={seedPrefix}
                onChange={(e) => setSeedPrefix(e.target.value.toUpperCase())}
                style={{ maxWidth: 180 }}
              />
              <input
                placeholder="Primul număr (ex: 1)"
                value={seedStart}
                onChange={(e) => setSeedStart(e.target.value.replace(/\D/g, ''))}
                style={{ maxWidth: 160 }}
              />
              <button className="review-btn" onClick={handleSeed} disabled={!seedStart}>
                Setează seria
              </button>
            </div>
            {manualSeed && (
              <p style={{ marginTop: '0.5rem', opacity: 0.7 }}>
                Serie setată manual: {manualSeed.prefix || '(fără prefix)'} de la {manualSeed.start}
                {manualSeed.set_by ? ` (de ${manualSeed.set_by})` : ''}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SpvSeriesPanel;
